import { CommonModule } from '@angular/common';
import { Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DateTime } from 'luxon';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';

@Component({
  selector: 'sb-luxon-datepicker',
  template: `
    <p-floatlabel [variant]="variant()" [class]="containerClass()">
      <p-datepicker
        [readonlyInput]="readonlyInput()"
        [inputId]="inputId()"
        [(ngModel)]="jsDate"
        [dateFormat]="dateFormat()"
        [appendTo]="appendTo()"
        [styleClass]="styleClass()"
        [class]="class() ?? ''"
        [panelStyleClass]="panelStyleClass()"
        [disabled]="isDisabled()"
        [showClear]="showClear()"
        [showIcon]="showIcon()"
        [showOnFocus]="showOnFocus()"
        [minDate]="minDate()"
        [maxDate]="maxDate()"
        (onSelect)="onDateSelect($event)"
        (onClear)="onClear()"
      ></p-datepicker>
      <label [for]="inputId()">{{ label() }}</label>
    </p-floatlabel>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LuxonDatepickerComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerModule, FloatLabelModule],
})
export class LuxonDatepickerComponent implements ControlValueAccessor {
  readonly readonlyInput = input<boolean>(false);
  readonly inputId = input<string>('');
  readonly label = input<string>('');
  readonly dateFormat = input<string>('dd. M. yy');
  readonly appendTo = input<string>('body');
  readonly styleClass = input<string>('!tw-w-full');
  readonly panelStyleClass = input<string>('!tw-min-w-0');
  readonly disabled = input<boolean>(false);
  readonly showClear = input<boolean>(true);
  readonly showIcon = input<boolean>(true);
  readonly showOnFocus = input<boolean>(true);
  readonly minDate = input<Date | null>(null);
  readonly maxDate = input<Date | null>(null);
  readonly variant = input<'in' | 'over' | 'on'>('on');
  readonly containerClass = input<string>('!tw-w-full');
  readonly class = input<string>(null);

  private internalDisabled = signal(false);

  // Create a computed signal that combines both values
  readonly isDisabled = computed(() => this.disabled() || this.internalDisabled());

  jsDate: Date | null = null;
  private onChange: any = () => {};
  private onTouched: any = () => {};

  // Convert from JavaScript Date to Luxon DateTime and notify form
  onDateSelect(date: Date): void {
    if (date) {
      const luxonDate = DateTime.fromJSDate(date);
      this.onChange(luxonDate);
    } else {
      this.onChange(null);
    }
    this.onTouched();
  }

  onClear(): void {
    this.jsDate = null;
    this.onChange(null);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value === null || value === undefined) {
      this.jsDate = null;
    } else if (value instanceof DateTime) {
      // Value is already a Luxon DateTime
      this.jsDate = value.toJSDate();
    } else if (typeof value === 'string') {
      // Try to parse string to DateTime
      const dt = DateTime.fromISO(value);
      this.jsDate = dt.isValid ? dt.toJSDate() : null;
    } else if (value instanceof Date) {
      // Value is a JavaScript Date
      this.jsDate = value;
    } else {
      console.warn('LuxonDatepickerComponent received unexpected value type:', value);
      this.jsDate = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalDisabled.set(isDisabled);
  }
}
